import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import dayjs from 'dayjs'
import Mixin from 'components/common/Mixin'
import Drawer from 'components/common/Drawer'
import Theme from 'components/common/Theme/Theme'
import { Field } from 'redux-form'
import { Input } from 'components/common/FormElements/Input'
import Media from 'components/common/Media'
import { injectProps } from 'helpers/utils'
import { SectionWrapper, ErrorMessage, ActionBarWrapper } from '../../../Stepper/components/common'
import { Heading } from '../common'
import UpdatingBlock from 'components/common/UpdatingBlock/UpdatingBlock'
import BOTDateTimeSelector from 'components/QuickAppointmentPage/BOTDateTimeSelector'
import StepperActionBar from '../../../Stepper/components/StepperActionBar'
import ThreeStepperDynamicTitle from '../ThreeStepperDynamicTitle'
import { WAIT_DROPOFF_HEADER, WAIT_DROPOFF_HEADER_ID } from 'components/AppointmentConfirmationPage/constants'
import model from './viewModel'
import Icon from 'components/common/Icon/Icon'
import { convDate } from '../../helpers'
import { DATEPICKER_THEME_COLOR_GREEN } from '../../constants'
import AppointmentDurationDisclosure from 'components/QuickAppointmentPage/BOTDateTimeSelector/components/DateTimeSelector/AppointmentDurationDisclosure'

const DateTimeAndVehicle = ({
  appointmentDetailsResponseVO,
  currentStepIndex,
  children: BOTVehicleSelector,
  error,
  handleSubmit,
  isLoading,
  submitFailed,
  showVehicleSelector,
  vehicleSelected,
  onPrevPage,
  submitAttemptFailed,
  title,
  calendarTitle,
  activePromotions,
  isQuickAppointmentPage
}) =>
  <form onSubmit={ handleSubmit }>
    {Boolean(activePromotions.length) && (
      <PromosStyledSectionWrapper>
        <PromotionsList>
          {activePromotions.map(({
            promotionId,
            promotionShortDescription,
            promotionExpiryDate
          }) => (
            <PromotionItem key={ promotionId }>
              <PromoIcon asset="tire-promo" width="18px" alt="Promo Icon" />
              <TextLabel>
                Potential Savings
              </TextLabel>
              <BoldText>{promotionShortDescription}</BoldText> <Text>Present offer in store. Expires {dayjs(convDate(promotionExpiryDate)).format('MM/DD/YYYY')}</Text>
            </PromotionItem>
          ))}

        </PromotionsList>
      </PromosStyledSectionWrapper>
    )}

    <StyledSectionWrapper>
      <StyledHeading data-at-appointment-headline>{ title }</StyledHeading>
      {isQuickAppointmentPage && (
        <Media.Mobile>
          <DurationInfo />
        </Media.Mobile>
      )}
      <DateTimeDrawer
        preventHandleBlur
        withHeading
        accordion
        expandedByDefault
        preventClose
        drawerProps={{
          CustomDrawerTitle: ThreeStepperDynamicTitle,
          title: calendarTitle,
          hideExpanderIcon: true,
          isDone: showVehicleSelector
        }}>
        <UpdatingBlock condition={ isLoading }>
          <BOTDateTimeSelector
            appointmentDetailsResponseVO={ appointmentDetailsResponseVO }
            noHeading
            isCheckout
            threeStepper
            theme={ DATEPICKER_THEME_COLOR_GREEN } />
        </UpdatingBlock>
      </DateTimeDrawer>

      <FieldWrapper>
        <legend><Media.ScreenReader>{ WAIT_DROPOFF_HEADER }</Media.ScreenReader></legend>
        <Headline id={ WAIT_DROPOFF_HEADER_ID } data-at-wait-dropoff-header>
          Would you like to …
        </Headline>
        <InputsWrapper>
          <SpanSpacer>
            <RadioInputField { ...model.userPlan.wait } component={ Input } />
          </SpanSpacer>
          <RadioInputField { ...model.userPlan.dropOff } component={ Input } />
        </InputsWrapper>
      </FieldWrapper>

      <StyledDrawer
        preventHandleBlur
        withHeading
        accordion
        expandedByDefault
        preventClose
        drawerProps={{
          CustomDrawerTitle: ThreeStepperDynamicTitle,
          title: 'Select Vehicle',
          hideExpanderIcon: true,
          isActive: showVehicleSelector,
          isDone: vehicleSelected
        }}>
        { injectProps(BOTVehicleSelector, { noHeading: true }) }
      </StyledDrawer>
      <ActionBarWrapper condensedBottom>
        <StepperActionBar
          hasPrevPage
          hasNextPage
          isPrevPageVisible
          alignRight
          currentStepIndex={ currentStepIndex }
          prevPageClick={ onPrevPage } />

        <ErrorMessage
          role="alert"
          aria-live="assertive"
          alignRight
          aria-label={ error }>
          { ((submitFailed || submitAttemptFailed) && error) || '' }
        </ErrorMessage>
      </ActionBarWrapper>
    </StyledSectionWrapper>
  </form>

DateTimeAndVehicle.propTypes = {
  activePromotions: PropTypes.arrayOf(),
  appointmentDetailsResponseVO: PropTypes.shape(),
  calendarTitle: PropTypes.string,
  children: PropTypes.node,
  currentStepIndex: PropTypes.number,
  error: PropTypes.string,
  handleSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  isQuickAppointmentPage: PropTypes.bool,
  onPrevPage: PropTypes.func,
  showVehicleSelector: PropTypes.bool,
  submitAttemptFailed: PropTypes.bool,
  submitFailed: PropTypes.bool,
  title: PropTypes.string,
  vehicleSelected: PropTypes.bool
}

export default DateTimeAndVehicle

const StyledHeading = styled(Heading)`
  ${Media.mobile`
    padding: 20px 0 5px;
  `}
`

const StyledDrawer = styled(Drawer)`
  border: none;
`

const DateTimeDrawer = styled(StyledDrawer)`
  padding: 20px 10px;
`

const StyledSectionWrapper = styled(SectionWrapper)`
  ${Mixin.responsive('padding', '0 100px', '0 0 30px 0')}
`

const PromosStyledSectionWrapper = styled(SectionWrapper)`
  ${Mixin.responsive('padding', '11px 100px', '10px')}
  ${Media.mobile`
    display: flex;
    justify-content: center;
  `}
`

const PromotionsList = styled.ul`
  width: 100%;
  margin: 0;
  padding: 13px 0 13px 36px;
  border: 1px solid ${Theme.colors.black};
  ${Media.mobile`
    width: fit-content;
    padding: 10px;
  `}
`

const PromotionItem = styled.li`
  display: flex;
  margin: 0;
  ${Media.mobile`
    position: relative;
    flex-direction: column;
  `}
`

const PromoIcon = styled(Icon)`
  margin: 0 15px 0 0;
  ${Media.mobile`
    position: absolute;
    margin: 0;
  `}
`

const Text = styled.p`
  margin: 0;
  padding: 0;
`

const BoldText = styled(Text)`
  font-weight: 600;
  margin: 0 5px 0 0;
`

const TextLabel = styled(BoldText)`
  ${Mixin.responsive('margin', '0 28px 0 0', '0 0 10px 25px')}
`

const Headline = styled.p`
  font-style: italic;
  margin-bottom: 20px;
`

const SpanSpacer = styled.span`
  position: relative;
  ${Mixin.responsive('margin-right', '32px', '16px')}

  &::after {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    height: 100%;
    content: '';
    background: ${Theme.colors.light_grey};
    ${Media.mobile`
      right: 0;
      left: 0;
      bottom: 0;
      top: unset;
      width: 100%;
      height: 2px;
    `}
  }
`

const FieldWrapper = styled.div`
  margin-bottom: 70px;
  ${Media.mobile`
    padding: 0 16px;
  `}
`

const RadioInputField = styled(Field)`
  padding: 7px 10px 7px 32px;
  border-radius: 5px;

  input:checked + .radio-input-text {
    &::after {
      background: ${Theme.colors.white};
      left: 16px;
      top: 10px;
      ${Media.mobile`
        top: 35px;
      `}
    }

    &::before {
      background: ${Theme.colors.green};
      left: 7px;
      border-color: ${Theme.colors.green};
      ${Media.mobile`
        top: 35px;
      `}
    }
  }

  input + .radio-input-text {
    ${Mixin.font.GothamMedium(20)}

    &::before {
      left: 5px;
      top: 10px;
      border-width: 2px;
      ${Media.mobile`
        top: 35px;
      `}
    }
  }

  input:checked + label {
    border-color: ${Theme.colors.green};
  }
`

const InputsWrapper = styled.div`
  display: flex;
  ${Media.mobile`
    flex-direction: column;
  `}

  & label {
    display: flex;
    flex-direction: column;
    max-width: 250px;
    margin: 0;
    padding: 0 25px 20px 35px;
    ${Media.mobile`
      padding: 25px 25px 20px 35px;
    `}
  }
`

const DurationInfo = styled(AppointmentDurationDisclosure)`
  margin: 15px 25px 17px;
`
